function defineCustomElement(name, constructor, options) {
  if (!customElements.get(name)) {
    customElements.define(name, constructor, options);
  }
}

class BasicHeader extends HTMLElement {
  constructor() {
    super();
    this.heightFrame = null;
    this.resizeObserver = null;
    this.designModeListeners = [];
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get headerNavigation() {
    return this.querySelector('.header__bottom');
  }

  get navigationToggleButton() {
    return this.querySelector('.toggle-navigation-button');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver && !this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.setHeight());
      this.resizeObserver.observe(this);
    }

    // window.addEventListener('resize', this.setMenuHeight.bind(this));

    if (Shopify.designMode && !this.designModeListeners.length) {
      const section = this.closest('.shopify-section');
      if (section) {
        ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((eventName) => {
          const handler = this.init.bind(this);
          section.addEventListener(eventName, handler);
          this.designModeListeners.push({ section, eventName, handler });
        });
      }
    }
  }

  disconnectedCallback() {
    if (this.heightFrame) {
      cancelAnimationFrame(this.heightFrame);
      this.heightFrame = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.designModeListeners.forEach(({ section, eventName, handler }) => {
      section.removeEventListener(eventName, handler);
    });
    this.designModeListeners = [];
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  calculateHeaderGroupHeight() {
    const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeight = 0;

    headerGroups.forEach((section) => {
      totalHeight += section.offsetHeight;
    });

    document.documentElement.style.setProperty('--header-group-height', `${totalHeight}px`);

    return totalHeight;
  }

  setHeight() {
    if (this.heightFrame) {
      cancelAnimationFrame(this.heightFrame);
    }

    this.heightFrame = requestAnimationFrame(() => {
      this.heightFrame = null;
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);
      const offsetNavigationHeight = Math.round(this.headerNavigation?.offsetHeight || 0);

      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
      document.documentElement.style.setProperty(
        '--header-navigation-height',
        `${Math.max(0, offsetNavigationHeight - 1)}px`
      );

      this.calculateHeaderGroupHeight();

      if (typeof this.refreshHeaderMetrics === 'function') {
        this.refreshHeaderMetrics();
      }
    });
  }
}

defineCustomElement('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    // Group CSS classes in one object for easier management
    this.classes = {
      pinned: 'header-pinned',
      headerScrolled: 'header-scrolled',
      show: 'is-show',
      hide: 'is-hide',
      headerSticky: 'header-sticky',
      isHideNav: 'is-hide-nav',
    };

    // Track navigation toggle state
    this.navigationManuallyToggled = false;

    // Initialize scroll tracking variables
    this.currentScrollTop = 0;

    // Add scroll threshold to prevent quick unpinning
    this.scrollThreshold = 96; // Avoid jitter while keeping the header responsive.
    this.scrollDirection = 'none';
    this.scrollDistance = 0;
    this.scrollFrame = null;
    this.queuedScrollTop = 0;
    this.scrollHandler = this.handleScroll.bind(this);
    this.navigationToggleHandler = this.handleNavigationToggle.bind(this);
    this.headerBoundsTop = 0;
    this.headerBoundsBottom = 0;
  }

  // Getters for easier property access
  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  get collapseOnScroll() {
    return this.dataset.collapseOnScroll === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = window.scrollY;
    this.refreshHeaderMetrics();

    // Initialize sticky header
    this.initStickyHeader();

    // Register toggle button event if needed
    if (this.collapseOnScroll && this.navigationToggleButton) {
      this.navigationToggleButton.addEventListener('click', this.navigationToggleHandler);
    }

    this.handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.scrollHandler);

    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }

    if (this.collapseOnScroll && this.navigationToggleButton) {
      this.navigationToggleButton.removeEventListener('click', this.navigationToggleHandler);
    }

    super.disconnectedCallback();
  }

  refreshHeaderMetrics() {
    if (!this.headerSection || !this.parentElement) return;

    const headerTop = Math.round(this.parentElement.offsetTop);
    const headerHeight = Math.round(this.headerSection.offsetHeight);

    this.headerBoundsTop = headerTop;
    this.headerBoundsBottom = headerTop + headerHeight;
  }

  // Initialize sticky header behavior
  initStickyHeader() {
    this.headerSection.classList.add(this.classes.headerSticky);
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.removeEventListener('scroll', this.scrollHandler);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  // Handle navigation toggle button click
  handleNavigationToggle(event) {
    event.preventDefault();

    this.navigationManuallyToggled = true;
    const isHidden = this.headerNavigation.classList.contains(this.classes.hide);

    // Toggle visibility state
    setTimeout(
      () => {
        document.body.classList.toggle(this.classes.isHideNav, !isHidden);
      },
      isHidden ? 0 : 250
    );
    this.headerNavigation.classList.toggle(this.classes.hide, !isHidden);
    this.headerNavigation.classList.toggle(this.classes.show, isHidden);
    this.navigationToggleButton.setAttribute('aria-expanded', isHidden);

    // Reset toggle state after delay
    setTimeout(() => {
      this.navigationManuallyToggled = false;
    }, 1000);
  }

  // Handle scroll events
  handleScroll() {
    this.queuedScrollTop = window.scrollY;

    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;

      const scrollTop = this.queuedScrollTop;
      const isScrolledPastHeader = scrollTop > this.headerBoundsTop;

      this.updateScrollMetrics(scrollTop);

      if (isScrolledPastHeader) {
        this.handleScrolledPastHeader(scrollTop, this.headerBoundsBottom);
      } else {
        this.handleScrolledBeforeHeader();
      }

      this.currentScrollTop = scrollTop;
    });
  }

  // Update scroll direction and accumulated distance
  updateScrollMetrics(scrollTop) {
    // Determine scroll direction
    const newDirection = scrollTop > this.currentScrollTop ? 'down' : 'up';

    // Reset accumulated distance when direction changes
    if (newDirection !== this.scrollDirection) {
      this.scrollDistance = 0;
      this.scrollDirection = newDirection;
    } else {
      // Accumulate scroll distance in the same direction
      this.scrollDistance += Math.abs(scrollTop - this.currentScrollTop);
    }
  }

  // Handle scroll position after passing the header
  handleScrolledPastHeader(scrollTop, headerBoundsBottom) {
    this.headerSection.classList.toggle(this.classes.headerScrolled, true);

    if (this.hasOpenInteraction()) {
      document.body.classList.add(this.classes.pinned);
      return;
    }

    // Handle collapse on scroll behavior
    if (this.collapseOnScroll && this.navigationToggleButton && this.headerNavigation) {
      this.navigationToggleButton.classList.toggle(this.classes.show, true);
      if (!this.navigationManuallyToggled && !this.headerNavigation.classList.contains(this.classes.show)) {
        this.headerNavigation.classList.toggle(this.classes.hide, true);
        document.body.classList.toggle(this.classes.isHideNav, true);
      }
    }

    // Handle sticky behavior based on configuration
    if (this.isAlwaysSticky) {
      document.body.classList.toggle(this.classes.pinned, true);
    } else {
      // Improved sticky on scroll up logic
      const isScrollingUp = this.scrollDirection === 'up';
      const isNearHeader = scrollTop < headerBoundsBottom + 100;
      const hasScrolledEnough = this.scrollDistance >= this.scrollThreshold;
      let shouldPin = document.body.classList.contains(this.classes.pinned);

      if (isScrollingUp || isNearHeader) {
        shouldPin = true;
      } else if (!this.navigationManuallyToggled && hasScrolledEnough) {
        // Only unpin when scrolled down enough and not manually toggled
        shouldPin = false;
      }

      document.body.classList.toggle(this.classes.pinned, shouldPin);
    }
  }

  // Handle scroll position before the header
  handleScrolledBeforeHeader() {
    this.headerSection.classList.toggle(this.classes.headerScrolled, false);

    if (this.collapseOnScroll && this.navigationToggleButton && this.headerNavigation) {
      if (!this.navigationManuallyToggled) {
        document.body.classList.toggle(this.classes.isHideNav, false);
        this.headerNavigation.classList.toggle(this.classes.hide, false);
        this.headerNavigation.classList.toggle(this.classes.show, false);
        this.navigationToggleButton.setAttribute('aria-expanded', false);
      }
      this.navigationToggleButton.classList.toggle(this.classes.show, false);
    }

    document.body.classList.remove(this.classes.pinned);
  }

  hasOpenInteraction() {
    return Boolean(
      this.querySelector('details[open], menu-drawer[open], predictive-search.predictive-search-open') ||
      document.body.classList.contains('search-open')
    );
  }
}

defineCustomElement('sticky-header', StickyHeader, { extends: 'header' });

const clearDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();
    // Initialize properties
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    // Reference to first and last child elements
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this.summaryElement?.setAttribute('aria-expanded', this.hasAttribute('open') ? 'true' : 'false');

    // Initial state based on attributes
    this._open = this.hasAttribute('open');

    // Event listeners for summary element
    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));
    
    if (FoxTheme.config.isTouch) {
      this.summaryElement.addEventListener(
        'touchstart',
        (event) => {
          // Prevent double-firing on touch devices
          this.touchStartTime = Date.now();
        },
        { passive: true }
      );
    }

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true;
        }
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false;
        }
      });
    }

    // Binding methods to ensure 'this' context is correct when they are called
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);

    // Setup hover detection with debouncing
    this.hoverEnterTimer = null;
    this.hoverLeaveTimer = null;
    // this.detectHoverListener = this.detectHover.bind(this);
    // this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    // this.addEventListener('mouseleave', this.detectHoverListener.bind(this));

    if (!FoxTheme.config.isTouch && this.trigger === 'hover') {
      this.addEventListener('mouseenter', (event) => {
        if (this.hoverLeaveTimer) {
          clearTimeout(this.hoverLeaveTimer);
          this.hoverLeaveTimer = null;
        }

        if (this.hoverEnterTimer) {
          clearTimeout(this.hoverEnterTimer);
          this.hoverEnterTimer = null;
        }

        // Keep a short intent threshold for the first menu, then switch quickly
        // between departments while the navigation layer is already active.
        const isVisualMenu = this.contentElement?.classList.contains('tm-visual-menu');
        const isSwitchingTopMenu = this.level === 'top' && document.body.classList.contains(this.classes.bodyClass);
        const hoverOpenDelay = isVisualMenu ? (isSwitchingTopMenu ? 40 : 90) : 0;
        if (hoverOpenDelay === 0) {
          this.detectHover({ type: 'mouseenter' });
          return;
        }

        this.hoverEnterTimer = setTimeout(() => {
          this.hoverEnterTimer = null;
          this.detectHover({ type: 'mouseenter' });
        }, hoverOpenDelay);
      });

      this.addEventListener('mouseleave', (event) => {
        if (this.hoverEnterTimer) {
          clearTimeout(this.hoverEnterTimer);
          this.hoverEnterTimer = null;
        }

        if (this.clickPinned) return;

        const hoverCloseDelay = this.contentElement?.classList.contains('tm-visual-menu') ? 120 : 60;
        this.hoverLeaveTimer = setTimeout(() => {
          this.detectHover({ type: 'mouseleave' });
        }, hoverCloseDelay);
      });
    }
  }

  set open(value) {
    // Check if the new value is different from the current value
    if (value !== this._open) {
      if (value && this.level === 'top') {
        const isVisualMenuSwitch = this.hasOpenVisualMenuPeer();

        if (isVisualMenuSwitch) {
          this.markVisualMenuSwitch();
        }

        this.closePeerMenus({ isVisualMenuSwitch });
      }

      // Update the internal state
      this._open = value;

      // Perform actions based on whether the element is connected to the DOM
      if (this.isConnected) {
        // If connected, perform a transition
        this.transition(value);
      } else {
        // If not connected, directly manipulate the 'open' attribute
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    // For touch devices, always use click events
    if (FoxTheme.config.isTouch) {
      return 'click';
    }

    // For non-touch devices, check for custom trigger attribute
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level');
    } else {
      return 'top';
    }
  }

  handleSummaryClick(event) {
    // Prevent the default action of the event
    event.preventDefault();

    const desktopCategoryLink =
      this.classList.contains('tm-category-menu') &&
      window.matchMedia('(min-width: 840px)').matches &&
      this.summaryElement.getAttribute('data-link');

    // Desktop category labels always behave as links. Hover remains responsible
    // for previewing the mega menu, independently of touch-capability detection.
    if (desktopCategoryLink) {
      this.clickPinned = false;
      window.location.assign(desktopCategoryLink);
      return;
    }

    // Check if the device is not touch-enabled and the trigger type is 'hover'
    if (!FoxTheme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
      // If conditions are met, navigate to the URL specified in 'data-link'
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else if (!FoxTheme.config.isTouch && this.trigger === 'hover') {
      // Focus opens hover menus before the click event. The first click pins the
      // already-open panel; a second click closes it.
      if (this.clickPinned) {
        this.clickPinned = false;
        this.open = false;
      } else {
        this.clickPinned = true;
        this.open = true;
      }
    } else {
      // Otherwise, toggle the 'open' state
      this.clickPinned = false;
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      // Increment the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) + 1);

      // Add class to body and set attributes
      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      this.summaryElement.setAttribute('aria-expanded', 'true');
      this.contentElement.setAttribute('open', '');

      // Add event listeners
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      // Perform the transition in animation
      await this.showWithTransition();

      const MenuProductList = this.querySelector('menu-product-list');
      if (MenuProductList && typeof MenuProductList === 'object') {
        MenuProductList.calcNavButtonsPosition();
      }

      // Check for reverse condition (not implemented here for brevity)
      this.needsReverse();

      // Wait for the after show event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      this.clickPinned = false;
      // Decrement the lock count for dropdowns
      clearDropdownCount.set(DetailsDropdown, Math.max(0, clearDropdownCount.get(DetailsDropdown) - 1));

      // Toggle class on body based on lock count
      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0);

      // Remove attributes and event listeners
      this.summaryElement.removeAttribute('open');
      this.summaryElement.setAttribute('aria-expanded', 'false');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);
      document.removeEventListener('focusout', this.handleFocusOut);

      // Perform the transition out animation
      await this.hideWithTransition();

      // Conditionally remove the 'open' attribute
      if (!this.open) {
        this.removeAttribute('open');
      }
      this.classList.remove('is-closing');

      // Wait for the after hide event
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    this.contentElement.style.opacity = '1';
    this.contentElement.style.visibility = 'visible';
    if (this.contentElement.firstElementChild) {
      this.contentElement.firstElementChild.style.transform = 'translateY(0)';
    }
    return Promise.resolve();
  }

  async hideWithTransition() {
    this.contentElement.style.opacity = '0';
    this.contentElement.style.visibility = 'hidden';
    if (this.contentElement.firstElementChild) {
      const translateY = this.level === 'top' ? '-105%' : '2rem';
      this.contentElement.firstElementChild.style.transform = `translateY(${translateY})`;
    }
    return Promise.resolve();
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        event.preventDefault();
        targetMenu.open = false;
        targetMenu.querySelector(':scope > summary')?.focus();
      }
    }
  }

  handleFocusOut(event) {
    if (this.clickPinned) return;
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      if (event.type === 'mouseenter') {
        if (!this.open) this.clickPinned = false;
        this.open = true;
      } else if (!this.clickPinned) {
        this.open = false;
      }
    }
  }

  hasOpenVisualMenuPeer() {
    if (!this.contentElement?.classList.contains('tm-visual-menu')) return false;

    const menu = this.closest('header') || document;

    return Array.from(menu.querySelectorAll('details[is="details-mega"][open]')).some((details) => {
      return (
        details !== this &&
        details.level === 'top' &&
        details.contentElement?.classList.contains('tm-visual-menu')
      );
    });
  }

  markVisualMenuSwitch() {
    window.cancelAnimationFrame(this.visualMenuSwitchFrame);
    window.cancelAnimationFrame(this.visualMenuSwitchCleanupFrame);
    this.classList.add('is-switching-menu');

    // Keep transitions disabled through one paint so both panels swap in the
    // same frame, then restore the regular first-open and close animations.
    this.visualMenuSwitchFrame = window.requestAnimationFrame(() => {
      this.visualMenuSwitchFrame = null;
      this.visualMenuSwitchCleanupFrame = window.requestAnimationFrame(() => {
        this.visualMenuSwitchCleanupFrame = null;
        this.classList.remove('is-switching-menu');
      });
    });
  }

  closePeerMenus({ isVisualMenuSwitch = false } = {}) {
    const menu = this.closest('header') || document;

    menu.querySelectorAll('details[is="details-dropdown"][open], details[is="details-mega"][open]').forEach((details) => {
      if (details === this || details.level !== 'top') return;

      if (details.hoverEnterTimer) {
        clearTimeout(details.hoverEnterTimer);
        details.hoverEnterTimer = null;
      }

      if (details.hoverLeaveTimer) {
        clearTimeout(details.hoverLeaveTimer);
        details.hoverLeaveTimer = null;
      }

      if (isVisualMenuSwitch && details.contentElement?.classList.contains('tm-visual-menu')) {
        details.markVisualMenuSwitch?.();

        if (details.open) {
          details.skipNextVisualMenuTransition = true;
        } else {
          // A panel can still carry the native `open` attribute while its leave
          // animation finishes. Complete that close now without arming a later one.
          details.megaMenuCloseCleanup?.();
          details.removeAttribute('open');
          details.classList.remove('is-closing');
        }
      }

      details.open = false;
    });

    menu.querySelectorAll('predictive-search.predictive-search-open').forEach((search) => {
      search.toggleSearchState?.(false);
    });
  }

  needsReverse() {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

defineCustomElement('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    this.updateViewportAlignment();

    if (this.contentElement.classList.contains('tm-visual-menu')) {
      this.megaMenuCloseCleanup?.();
      this.classList.remove('is-closing');
      this.contentElement.style.removeProperty('opacity');
      this.contentElement.style.removeProperty('visibility');
      if (this.contentElement.firstElementChild) {
        this.contentElement.firstElementChild.style.removeProperty('visibility');
        this.contentElement.firstElementChild.style.removeProperty('transform');
      }
      return Promise.resolve();
    }

    if (this.contentElement.firstElementChild) {
      this.contentElement.firstElementChild.style.visibility = 'visible';
      this.contentElement.firstElementChild.style.transform = 'translateY(0)';
    }
    return Promise.resolve();
  }

  async hideWithTransition() {
    if (this.contentElement.classList.contains('tm-visual-menu')) {
      this.contentElement.style.removeProperty('opacity');
      this.contentElement.style.removeProperty('visibility');
      if (this.contentElement.firstElementChild) {
        this.contentElement.firstElementChild.style.removeProperty('visibility');
        this.contentElement.firstElementChild.style.removeProperty('transform');
      }

      if (this.skipNextVisualMenuTransition) {
        this.skipNextVisualMenuTransition = false;
        this.megaMenuCloseCleanup?.();
        this.classList.remove('is-closing');
        this.removeAttribute('open');
        return Promise.resolve();
      }

      // Keep the native <details> open during the composited leave transition.
      // Removing its attribute sooner makes the browser hide the panel before
      // transform and opacity can finish, which produces an abrupt disappearance.
      return new Promise((resolve) => {
        let settled = false;

        const finish = () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(this.megaMenuCloseTimer);
          this.contentElement.removeEventListener('transitionend', handleTransitionEnd);
          this.contentElement.removeEventListener('transitioncancel', finish);
          this.megaMenuCloseCleanup = null;
          resolve();
        };

        const handleTransitionEnd = (event) => {
          if (event.target === this.contentElement && event.propertyName === 'transform') finish();
        };

        this.megaMenuCloseCleanup?.();
        this.megaMenuCloseCleanup = finish;
        this.contentElement.addEventListener('transitionend', handleTransitionEnd);
        this.contentElement.addEventListener('transitioncancel', finish);
        this.classList.add('is-closing');
        this.megaMenuCloseTimer = window.setTimeout(finish, 220);
      });
    }

    if (this.contentElement.firstElementChild) {
      this.contentElement.firstElementChild.style.visibility = 'hidden';
      this.contentElement.firstElementChild.style.transform = 'translateY(-100%)';
    }
    return Promise.resolve();
  }

  updateViewportAlignment() {
    if (!this.contentElement || !window.matchMedia('(min-width: 840px)').matches) return;

    if (this.contentElement.classList.contains('tm-visual-menu')) {
      this.contentElement.style.setProperty('--mega-menu-correction-x', '0px');
      return;
    }

    this.contentElement.style.setProperty('--mega-menu-correction-x', '0px');

    const menuRect = this.contentElement.getBoundingClientRect();
    const correctionX = Math.round(menuRect.left * -1);

    this.contentElement.style.setProperty('--mega-menu-correction-x', `${correctionX}px`);
  }
}

defineCustomElement('details-mega', DetailsMega, { extends: 'details' });

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();
    this.submenu = this.querySelector(':scope > .menu-drawer__submenu');
    this.onToggleHandler = this.syncState.bind(this);
    this.onCloseButtonHandler = this.onCloseButtonClick.bind(this);

    if (this.submenu && !this.submenu.id) {
      MenuDrawerDetails.submenuCount += 1;
      this.submenu.id = `MenuDrawerSubmenu-${MenuDrawerDetails.submenuCount}`;
    }

    if (this.submenu) this.summary.setAttribute('aria-controls', this.submenu.id);
    this.summary.setAttribute('aria-expanded', this.open ? 'true' : 'false');
    this.addEventListener('toggle', this.onToggleHandler);
    this.closeButton?.addEventListener('click', this.onCloseButtonHandler);
    this.syncState();
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.submenu?.querySelector('.menu-drawer__item-back-link');
  }

  disconnectedCallback() {
    this.removeEventListener('toggle', this.onToggleHandler);
    this.closeButton?.removeEventListener('click', this.onCloseButtonHandler);
  }

  syncState() {
    const isOpen = this.open;
    this.parent?.classList.toggle('active', isOpen);
    this.classList.toggle('active', isOpen);
    this.summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (this.submenu) {
      this.submenu.hidden = !isOpen;
      this.submenu.toggleAttribute('inert', !isOpen);
      this.submenu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      if (isOpen) this.closest('menu-drawer')?.prepareImages(this.submenu, 'auto');
    }
  }

  onCloseButtonClick(event) {
    event.preventDefault();
    this.close({ restoreFocus: true });
  }

  close({ restoreFocus = false } = {}) {
    if (!this.open) return;
    this.open = false;
    this.syncState();
    if (restoreFocus) this.summary.focus();
  }
}
MenuDrawerDetails.submenuCount = 0;
defineCustomElement('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
    this.handleVisibleFocusTrap = this.handleVisibleFocusTrap.bind(this);
    this.handleNestedAccordionClick = this.handleNestedAccordionClick.bind(this);
    this.handleNestedAccordionToggle = this.handleNestedAccordionToggle.bind(this);
    this.drawerTransitionCleanup = null;
    this.modalStateCommitted = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupNestedAccordions();
    this.addEventListener('click', this.handleNestedAccordionClick, true);
    this.addEventListener('toggle', this.handleNestedAccordionToggle, true);
  }

  disconnectedCallback() {
    this.cancelDrawerTransition();
    this.removeEventListener('click', this.handleNestedAccordionClick, true);
    this.removeEventListener('toggle', this.handleNestedAccordionToggle, true);
    this.removeEventListener('keydown', this.handleVisibleFocusTrap);
    if (this.modalStateCommitted) {
      this.modalStateCommitted = false;
      super.handleAfterHide();
    } else {
      document.body.classList.remove(this.classes.showing);
    }
    super.disconnectedCallback();
  }

  get header() {
    return this.closest('header');
  }

  // get menuItems() {
  //   if (!this._menuItems) {
  //     this._menuItems = this.querySelectorAll(
  //       '.menu-drawer__menus:not(.active) > ul > li, .menu-drawer__menus:not(.active) > .menu-drawer__footer'
  //     );
  //   }
  //   return this._menuItems;
  // }

  get requiresBodyAppended() {
    return false;
  }

  get focusElement() {
    return this.querySelector('[data-menu-drawer-close]') || this.querySelector('a[href]') || this;
  }

  get drawerInner() {
    return this.querySelector(':scope > .drawer__inner');
  }

  get drawerTransitionDuration() {
    return 300;
  }

  get prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
  }

  cancelDrawerTransition() {
    this.drawerTransitionCleanup?.();
  }

  hasTransformTransition(element) {
    const styles = window.getComputedStyle(element);
    const properties = styles.transitionProperty.split(',').map((property) => property.trim());
    const durations = styles.transitionDuration.split(',').map((duration) => {
      const value = parseFloat(duration) || 0;
      return duration.trim().endsWith('ms') ? value : value * 1000;
    });

    return properties.some((property, index) => {
      const duration = durations[index % durations.length] || 0;
      return (property === 'transform' || property === 'all') && duration > 0;
    });
  }

  runDrawerTransition(startTransition, { animate = true, defer = false } = {}) {
    this.cancelDrawerTransition();

    const drawerInner = this.drawerInner;
    if (!animate || this.prefersReducedMotion || !drawerInner) {
      startTransition();
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let animationFrame = null;
      let timeout = null;
      let isListening = false;
      let isFinished = false;

      const cleanup = () => {
        if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
        if (timeout !== null) window.clearTimeout(timeout);
        if (isListening) {
          drawerInner.removeEventListener('transitionend', handleTransitionComplete);
          drawerInner.removeEventListener('transitioncancel', handleTransitionComplete);
        }
        if (this.drawerTransitionCleanup === finish) this.drawerTransitionCleanup = null;
      };

      const finish = () => {
        if (isFinished) return;
        isFinished = true;
        cleanup();
        resolve();
      };

      const handleTransitionComplete = (event) => {
        if (event.target !== drawerInner || event.propertyName !== 'transform') return;
        finish();
      };

      const begin = () => {
        animationFrame = null;
        if (!this.isConnected) {
          finish();
          return;
        }

        drawerInner.addEventListener('transitionend', handleTransitionComplete);
        drawerInner.addEventListener('transitioncancel', handleTransitionComplete);
        isListening = true;
        startTransition();

        if (!this.hasTransformTransition(drawerInner)) {
          finish();
          return;
        }

        timeout = window.setTimeout(finish, this.drawerTransitionDuration + 50);
      };

      this.drawerTransitionCleanup = finish;
      if (defer) animationFrame = window.requestAnimationFrame(begin);
      else begin();
    });
  }

  handleShowTransition(animate = true) {
    if (this.hasAttribute('active')) {
      this.cancelDrawerTransition();
      return Promise.resolve();
    }

    return this.runDrawerTransition(() => this.setAttribute('active', ''), { animate, defer: animate });
  }

  handleHideTransition() {
    if (!this.hasAttribute('active')) {
      this.cancelDrawerTransition();
      return Promise.resolve();
    }

    return this.runDrawerTransition(() => this.removeAttribute('active'));
  }

  getVisibleFocusableElements() {
    const activeSubmenu = this.querySelector('details[is="menu-drawer-details"].active > .menu-drawer__submenu:not([hidden])');

    return FoxTheme.a11y.getFocusableElements(this).filter((element) => {
      if (element.closest('[hidden], [inert]')) return false;
      if (element === this.focusElement) return true;
      if (activeSubmenu) return activeSubmenu.contains(element);
      return !element.closest('.menu-drawer__submenu');
    });
  }

  setupNestedAccordions() {
    this.querySelectorAll('details[is="accordion-details"]').forEach((details, index) => {
      const content = details.querySelector(':scope > summary + *');
      if (!content) return;
      if (!content.id) content.id = `MenuDrawerAccordion-${index + 1}`;
      this.syncNestedAccordion(details, details.open);
    });
  }

  syncNestedAccordion(details, isOpen) {
    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector(':scope > summary + *');
    if (!summary || !content) return;

    summary.setAttribute('aria-controls', content.id);
    summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    content.hidden = !isOpen;
    content.toggleAttribute('inert', !isOpen);
    content.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
  }

  handleNestedAccordionClick(event) {
    const summary = event.target.closest?.('details[is="accordion-details"] > summary');
    if (!summary || !this.contains(summary)) return;

    const details = summary.parentElement;
    const willOpen = !details.open;
    if (willOpen) this.syncNestedAccordion(details, true);
    else summary.setAttribute('aria-expanded', 'false');
  }

  handleNestedAccordionToggle(event) {
    const details = event.target;
    if (!details.matches?.('details[is="accordion-details"]')) return;
    this.syncNestedAccordion(details, details.open);
  }

  handleVisibleFocusTrap(event) {
    if (event.key !== 'Tab') return;
    const focusableElements = this.getVisibleFocusableElements();
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  prepareImages(container = this, priority = 'low') {
    container.querySelectorAll('.tm-collection-card__media img, .tm-brand-logo-card__image').forEach((image) => {
      // Start hidden category images on menu intent; prioritize only the open category.
      if (priority === 'auto' || image.loading === 'lazy') {
        image.setAttribute('fetchpriority', priority);
      }
      if (image.loading === 'lazy') image.loading = 'eager';
    });
  }

  prepareToShow() {
    super.prepareToShow();
    this.prepareImages();
    this.header
      ?.querySelectorAll('details[is="details-dropdown"][open], details[is="details-mega"][open]')
      .forEach((details) => (details.open = false));
    this.querySelectorAll('details[is="menu-drawer-details"]').forEach((menu) => menu.close());
    this.querySelectorAll('details[is="accordion-details"]').forEach((details) => {
      if (details.open) details.open = false;
      this.syncNestedAccordion(details, false);
    });

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    document.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
  }

  handleAfterShow() {
    if (!this.open || !this.isConnected) return;

    if (!this.modalStateCommitted) {
      super.handleAfterShow();
      this.modalStateCommitted = true;
    } else {
      document.body.classList.remove(this.classes.showing);
      document.body.classList.add(this.classes.show);
    }
    FoxTheme.a11y.removeTrapFocus();
    this.addEventListener('keydown', this.handleVisibleFocusTrap);
    this.focusElement.focus();
  }

  // animateMenuItems() {
  //   FoxTheme.Motion.animate(
  //     this.menuItems,
  //     {
  //       transform: ['translateX(-20px)', 'translateX(0)'],
  //       opacity: [0, 1],
  //     },
  //     {
  //       duration: 0.6,
  //       easing: [0.075, 0.82, 0.165, 1],
  //       delay: FoxTheme.Motion.stagger(0.1),
  //     }
  //   ).finished.then(() => {
  //     this.menuItems.forEach((item) => item.removeAttribute('style'));
  //   });
  // }

  handleAfterHide() {
    if (this.open) return;

    this.removeEventListener('keydown', this.handleVisibleFocusTrap);
    this.querySelectorAll('details[is="menu-drawer-details"]').forEach((menu) => menu.close());
    if (this.modalStateCommitted) {
      this.modalStateCommitted = false;
      super.handleAfterHide();
    } else {
      document.body.classList.remove(this.classes.showing);
      FoxTheme.a11y.removeTrapFocus(this.activeElement);
    }
    document.dispatchEvent(new CustomEvent('menu-drawer:close', { bubbles: true }));
  }
}
defineCustomElement('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    if (!this.container) return;

    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }

  calcNavButtonsPosition() {
    if (this.dataset.calcButtonPosition !== 'true') return;

    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }
}
defineCustomElement('menu-product-list', MenuProductList);

class MenuSidebar extends HTMLElement {
  constructor() {
    super();
    this.handleSidenavMenuToggle = this.handleSidenavMenuToggle.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
  }

  get summarys() {
    return this.querySelectorAll('summary');
  }

  connectedCallback() {
    this.setInitialMinHeight();

    const firstSummary = this.summarys[0];
    if (firstSummary) {
      this.setActiveItem(firstSummary, false);
    }

    this.summarys.forEach((summary) => {
      summary.addEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.addEventListener('click', (e) => e.preventDefault());
    });

    this.setupIntersectionObserver();
  }

  setInitialMinHeight() {
    const container = this.closest('.mega-menu__wrapper');
    if (container) {
      const parentHeight = container.parentElement.offsetHeight;
      container.style.setProperty('--menu-sidebar-height', `${parentHeight}px`);
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.updateHeight();
        }
      });
    });

    observer.observe(this);
  }

  updateHeight() {
    const activeSummary = this.querySelector('.is-visible');
    if (activeSummary) {
      const container = this.closest('.mega-menu__wrapper');
      const content = activeSummary.nextElementSibling;
      if (container && content) {
        container.style.setProperty('--sidebar-height', `${content.scrollHeight}px`);
      }
    }
  }

  setActiveItem(summaryElem, updateHeight = true) {
    const lastSidenavElem = this.querySelector('.is-visible');
    if (lastSidenavElem) {
      lastSidenavElem.classList.remove('is-visible');
    }

    summaryElem.classList.add('is-visible');

    if (updateHeight) {
      this.updateHeight();
    }
  }

  handleSidenavMenuToggle(evt) {
    const summaryElem = evt.target;
    this.setActiveItem(summaryElem);
  }

  disconnectedCallback() {
    this.summarys.forEach((summary) => {
      summary.removeEventListener('mouseenter', this.handleSidenavMenuToggle);
      summary.removeEventListener('click', this.handleClick);
    });
  }
}

defineCustomElement('menu-sidebar', MenuSidebar);

function initTodayMerchSmartHeader(root = document) {
  root.querySelectorAll('header[data-tm-smart-sticky]').forEach((header) => {
    if (header.dataset.tmSmartStickyBound === 'true') return;

    const section = header.closest('.header-section');
    if (!section) return;

    header.dataset.tmSmartStickyBound = 'true';
    const headerOriginTop = Math.round(section.offsetTop);
    const stickyMode = header.dataset.tmStickyMode || 'on-scroll-up';
    const isAlwaysPinned = stickyMode === 'always';
    section.classList.add('header-sticky');
    section.dataset.stickyType = stickyMode;

    let lastScrollTop = Math.max(0, window.scrollY);
    let queuedScrollTop = lastScrollTop;
    let scrollFrame = null;

    const hasOpenInteraction = () =>
      Boolean(
        header.querySelector('details[open], menu-drawer[open], predictive-search.predictive-search-open') ||
        document.body.classList.contains('search-open')
      );

    const syncOverlayAnchor = () => {
      const headerBottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      header.style.setProperty('--tm-header-anchor-bottom', `${headerBottom}px`);
    };

    const update = () => {
      scrollFrame = null;
      const scrollTop = queuedScrollTop;
      const headerTop = headerOriginTop;
      const hideThreshold = headerTop + Math.round(header.offsetHeight) + 48;
      const delta = scrollTop - lastScrollTop;
      const isPastHeader = scrollTop > headerTop;

      section.classList.toggle('header-scrolled', isPastHeader);

      if (!isPastHeader) {
        document.body.classList.remove('header-pinned');
      } else if (isAlwaysPinned || hasOpenInteraction() || delta < -2) {
        document.body.classList.add('header-pinned');
      } else if (delta > 2 && scrollTop > hideThreshold) {
        document.body.classList.remove('header-pinned');
      }

      syncOverlayAnchor();
      lastScrollTop = scrollTop;
    };

    const requestUpdate = () => {
      queuedScrollTop = Math.max(0, window.scrollY);
      if (scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    header.addEventListener('toggle', requestUpdate, true);
    header.addEventListener('focusin', () => {
      document.body.classList.add('header-pinned');
      requestUpdate();
    });
    header.addEventListener('transitionend', (event) => {
      if (event.target === header && event.propertyName === 'transform') requestUpdate();
    });
    header.addEventListener('menu-drawer:open', () => document.body.classList.add('header-pinned'));
    header.addEventListener('menu-drawer:close', requestUpdate);
    requestUpdate();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTodayMerchSmartHeader(), { once: true });
} else {
  initTodayMerchSmartHeader();
}

document.addEventListener('shopify:section:load', (event) => initTodayMerchSmartHeader(event.target));

document.addEventListener('click', (event) => {
  const chatTrigger = event.target.closest('[data-contact-hub-chat-trigger]');

  if (!chatTrigger || event.defaultPrevented) return;

  if (typeof window.TodayMerchIntercom?.open === 'function') {
    event.preventDefault();
    window.TodayMerchIntercom.open({ trigger: chatTrigger });
    return;
  }

  if (typeof window.Intercom !== 'function') return;

  event.preventDefault();
  window.Intercom('showNewMessage');
});
